// src/components/lists/AccountsList/LedgerList.tsx

import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { LedgerEntry } from '../../../services/api';
import AccountsList, {
  Column,
  DateCell,
  ChipCell,
  CurrencyCell,
  LinkCell,
  TextCell,
} from './AccountsList';
import { LedgerItem, typeColor, formatCurrency } from './accountTypes';

interface LedgerListProps {
  ledger: LedgerEntry[];
}

const typeLabel = (type: string): string =>
  type === 'OpeningBalance' ? 'Opening' : type;

const LedgerList: React.FC<LedgerListProps> = ({ ledger }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const ledgerColumns: Column<LedgerItem>[] = [
    {
      field: 'date',
      label: 'DATE',
      width: 175,
      render: (item) => <DateCell>{item.date}</DateCell>,
    },
    {
      field: 'type',
      label: 'METHOD',
      width: 155,
      render: (item) => (
        <ChipCell>
          <Chip
            label={typeLabel(item.type)}
            size="small"
            color={typeColor(item.type)}
          />
        </ChipCell>
      ),
    },
    {
      field: 'description',
      label: 'DESCRIPTION',
      width: 260,
      mobileWidth: 400,
      flex: 1,
      render: (item) => (
        <TextCell>
          <span>{item.description}</span>
          {item.transactionReference && (
            <span
              style={{
                fontSize: '0.75em',
                opacity: 0.6,
                lineHeight: 1.2,
                display: 'block',
              }}
            >
              Ref: {item.transactionReference}
            </span>
          )}
        </TextCell>
      ),
    },
    {
      field: 'ref',
      label: 'REF',
      width: 105,
      render: (item) => (
        <LinkCell>
          {item.ref && (
            <Button
              size="small"
              variant="text"
              onClick={() => navigate(`/booking/${item.ref}`)}
            >
              #{item.ref}
            </Button>
          )}
        </LinkCell>
      ),
    },
    {
      field: 'debit',
      label: 'DEBIT',
      width: 195,
      render: (item) => (
        <CurrencyCell sx={{ color: theme.palette.error.main }}>
          {formatCurrency(item.debit)}
        </CurrencyCell>
      ),
    },
    {
      field: 'credit',
      label: 'CREDIT',
      width: 195,
      render: (item) => (
        <CurrencyCell sx={{ color: theme.palette.success.main }}>
          {formatCurrency(item.credit)}
        </CurrencyCell>
      ),
    },
    {
      field: 'balance',
      label: 'BALANCE',
      width: 210,
      render: (item) => (
        <CurrencyCell>{formatCurrency(item.balance)}</CurrencyCell>
      ),
    },
  ];

  const ledgerItems: LedgerItem[] = ledger.map((row, idx) => ({
    id: idx,
    date: row.date.split('T')[0],
    type: row.type,
    description: row.description,
    transactionReference: row.transactionReference,
    ref: row.bookingRef?.toString() ?? '',
    debit: row.debit,
    credit: row.credit,
    balance: row.balance,
  }));

  if (ledger.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <AccountBalanceIcon sx={{ fontSize: '3rem', opacity: 0.8, mb: 1 }} />
        <Typography variant="body1" sx={{ fontSize: '2rem' }}>
          No transactions yet
        </Typography>
      </Box>
    );
  }

  return <AccountsList items={ledgerItems} columns={ledgerColumns} />;
};

export default LedgerList;
